import { useState, useEffect, useCallback } from 'react';
import { type ColumnConfig } from '@/components/collections/column-config-dialog';

const STORAGE_KEY = 'promise-to-pay-column-preferences';

// Default column configuration - this defines the original view
export const DEFAULT_PROMISE_COLUMNS: ColumnConfig[] = [
  { id: 'select', label: 'Select', visible: true, order: 0, isDefault: true, category: 'basic' },
  { id: 'promiseId', label: 'Promise ID', visible: true, order: 1, isDefault: true, category: 'basic' },
  { id: 'customerName', label: 'Customer', visible: true, order: 2, isDefault: true, category: 'basic' },
  { id: 'caseNumber', label: 'Case Number', visible: true, order: 3, isDefault: true, category: 'basic' },
  { id: 'lastAction', label: 'Last Action', visible: true, order: 4, isDefault: true, category: 'details' },
  { id: 'promiseDate', label: 'Promise Date', visible: true, order: 5, isDefault: true, category: 'dates' },
  { id: 'daysOverdue', label: 'Days Overdue', visible: true, order: 6, isDefault: true, category: 'dates' },
  { id: 'amount', label: 'Amount', visible: true, order: 7, isDefault: true, category: 'details' },
  { id: 'transactions', label: 'Transactions', visible: true, order: 8, isDefault: true, category: 'details' },
  { id: 'status', label: 'Status', visible: true, order: 9, isDefault: true, category: 'status' },
  { id: 'method', label: 'Method', visible: true, order: 10, isDefault: true, category: 'details' },
  // Additional columns available but not shown by default
  { id: 'customerAccountNumber', label: 'Account Number', visible: false, order: 11, isDefault: false, category: 'basic' },
  { id: 'customerEmail', label: 'Email', visible: false, order: 12, isDefault: false, category: 'basic' },
  { id: 'customerPhone', label: 'Phone', visible: false, order: 13, isDefault: false, category: 'basic' },
  { id: 'createdDate', label: 'Created Date', visible: false, order: 14, isDefault: false, category: 'dates' },
  { id: 'createdBy', label: 'Created By', visible: false, order: 15, isDefault: false, category: 'details' },
  { id: 'notes', label: 'Notes', visible: false, order: 16, isDefault: false, category: 'details' },
];

export function usePromiseColumnPreferences() {
  const [columns, setColumns] = useState<ColumnConfig[]>(DEFAULT_PROMISE_COLUMNS);
  const [isLoading, setIsLoading] = useState(true);

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ColumnConfig[];
        // Merge with defaults to handle any new columns added in updates
        const merged = mergeWithDefaults(parsed, DEFAULT_PROMISE_COLUMNS);
        setColumns(merged);
      }
    } catch (error) {
      console.error('Failed to load promise column preferences:', error);
      // Fall back to defaults on error
      setColumns(DEFAULT_PROMISE_COLUMNS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save preferences to localStorage
  const savePreferences = useCallback((newColumns: ColumnConfig[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newColumns));
      setColumns(newColumns);
    } catch (error) {
      console.error('Failed to save promise column preferences:', error);
    }
  }, []);

  // Reset to default configuration
  const resetToDefaults = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setColumns(DEFAULT_PROMISE_COLUMNS);
    } catch (error) {
      console.error('Failed to reset promise column preferences:', error);
    }
  }, []);

  // Get visible columns in order
  const visibleColumns = columns
    .filter((col) => col.visible)
    .sort((a, b) => a.order - b.order);

  return {
    columns,
    visibleColumns,
    isLoading,
    savePreferences,
    resetToDefaults,
  };
}

// Helper function to merge stored preferences with defaults
// This ensures new columns are added when the app is updated
function mergeWithDefaults(
  stored: ColumnConfig[],
  defaults: ColumnConfig[]
): ColumnConfig[] {
  const storedMap = new Map(stored.map((col) => [col.id, col]));
  
  return defaults.map((defaultCol) => {
    const storedCol = storedMap.get(defaultCol.id);
    if (storedCol) {
      // Use stored preferences but keep default metadata
      return {
        ...defaultCol,
        visible: storedCol.visible,
        order: storedCol.order,
      };
    }
    // New column not in stored preferences, use default
    return defaultCol;
  });
}
